/**
 * Open Belot 1×1: deal 6+10×2, 2-player auction, play with logical seats 0..3
 * (hand/table per player) reusing {@link ../armenian/tricks}.
 */

const { TRUMP_NO_TRUMP, TRUMP_ALL_TRUMP, KAPOT_CONTRACT_POINTS } = require("../armenian/auction");
const { teamOfSeat } = require("./types");
const {
  cardPointsTowardRoundTotal,
  legalPlays,
  trickWinner,
} = require("../armenian/tricks");
const { scoreRound } = require("../armenian/scoring");
const { dealOpenDuell } = require("./dealOpenLayout");
const { createInitialAuction2p, applyAuction2pCommand } = require("./auction2p");
const { resolveFirstTrickOpenDeclarations } = require("./openDuellDeclarations");

/** Внутренние пороги (полные очки); в UI CP = значение / 10 → 501 / 1001. */
/** @type {readonly [5010, 10010]} */
const ALLOWED_TARGET_SCORES = Object.freeze([5010, 10010]);
const DEFAULT_TARGET_SCORE = 10010;

/**
 * @param {unknown} value
 * @returns {value is 5010|10010}
 */
function isAllowedTargetScore(value) {
  return value === 5010 || value === 10010;
}

/**
 * @param {unknown} config
 * @returns {{ targetScore: 5010|10010, rng: () => number }}
 */
function normalizeEngineConfig(config) {
  const raw = config && typeof config === "object" ? config : {};
  const ts = raw.targetScore;
  const targetScore = ts === undefined ? DEFAULT_TARGET_SCORE : Number(ts);
  if (!isAllowedTargetScore(targetScore)) {
    throw new Error(`openDuell/engine: targetScore must be 5010 or 10010, got ${String(ts)}`);
  }
  const rng = typeof raw.rng === "function" ? raw.rng : () => Math.random();
  return { targetScore, rng };
}

/**
 * @param {number[][]} stacksByCol — 5 столбцов, каждый — массив карт снизу вверх
 * @returns {number[][]}
 */
function cloneTableStacks(stacksByCol) {
  return stacksByCol.map((stack) => stack.slice());
}

/**
 * @param {Array<{ bottom: number, top: number }>} cols
 * @returns {number[][]}
 */
function columnsToStacks(cols) {
  return cols.map(({ bottom, top }) => [bottom, top]);
}

/**
 * @param {any} s
 * @returns {boolean}
 */
function hasMatchWinner(s) {
  return s.teamScores[0] >= s.targetScore || s.teamScores[1] >= s.targetScore;
}

/**
 * @param {number[][]} tableStacksOnePlayer
 * @returns {number[]}
 */
function topCardsFromTable(tableStacksOnePlayer) {
  const out = [];
  if (!Array.isArray(tableStacksOnePlayer)) return out;
  for (const stack of tableStacksOnePlayer) {
    if (Array.isArray(stack) && stack.length) out.push(stack[stack.length - 1]);
  }
  return out;
}

/**
 * @param {number[][]} tableStacksOnePlayer
 * @param {number} card
 * @returns {number}
 */
function columnIndexForTopCard(tableStacksOnePlayer, card) {
  if (!Array.isArray(tableStacksOnePlayer)) return -1;
  for (let i = 0; i < tableStacksOnePlayer.length; i += 1) {
    const st = tableStacksOnePlayer[i];
    if (Array.isArray(st) && st.length && st[st.length - 1] === card) return i;
  }
  return -1;
}

/**
 * Места 0/1 — ход с руки; если рука пуста (взятки 7–8), тот же игрок ходит со стола → логическое место 2 или 3.
 *
 * @param {any} state
 * @param {number} logicalSeat 0..3
 * @returns {number}
 */
function normalizeTurnSeatForOpenDuellEmptyHand(state, logicalSeat) {
  if (logicalSeat !== 0 && logicalSeat !== 1) return logicalSeat;
  const ph = logicalSeat === 0 ? 0 : 1;
  const hand = state.hands?.[ph];
  if (!Array.isArray(hand) || hand.length > 0) return logicalSeat;
  return logicalSeat + 2;
}

/**
 * Следующее место по кругу 0→1→2→3 с учётом пустой руки на 0/1.
 *
 * @param {any} state
 * @param {number} afterLogicalSeat
 * @returns {number}
 */
function nextOpenDuellPlaySeat(state, afterLogicalSeat) {
  const raw = (afterLogicalSeat + 1) % 4;
  return normalizeTurnSeatForOpenDuellEmptyHand(state, raw);
}

/**
 * Следующий ход в незавершённой взятке: k-й слот — место (trickLeadSeat + k) mod 4
 * (k = trickLengthAfterPlay — число карт уже на взятке, включая только что сыгранную).
 * Нельзя брать «(последний logicalSeat + 1)»: при пустой руке logicalSeat 2 — тот же слот, что номинальный 0.
 *
 * @param {any} state
 * @param {number} trickLeadSeat
 * @param {number} trickLengthAfterPlay
 * @returns {number}
 */
function nextOpenDuellPartialTrickTurnSeat(state, trickLeadSeat, trickLengthAfterPlay) {
  const raw = (Number(trickLeadSeat) + Number(trickLengthAfterPlay)) % 4;
  return normalizeTurnSeatForOpenDuellEmptyHand(state, raw);
}

/**
 * @param {Partial<{ targetScore: number, rng: () => number }>} [config]
 */
function createInitialState(config) {
  const { targetScore, rng } = normalizeEngineConfig(config || {});
  return {
    phase: "init",
    targetScore,
    rng,
    dealerSeat: /** @type {0|1} */ (0),
    teamScores: /** @type {[number, number]} */ ([0, 0]),
    roundIndex: 0,
    hands: /** @type {[number[]|null, number[]|null]} */ ([null, null]),
    tableStacks: /** @type {[number[][]|null, number[][]|null]} */ ([null, null]),
    auction: null,
    contract: null,
    trickIndex: 0,
    trickLeadSeat: /** @type {number|null} */ (null),
    currentTrick: /** @type {{ seat: number, card: number }[]} */ ([]),
    currentTurnSeat: /** @type {number|null} */ (null),
    tricksWonByTeam: /** @type {[number, number]} */ ([0, 0]),
    trickCardsByTeam: /** @type {[number[], number[]]} */ ([[], []]),
    lastTrickWinnerSeat: /** @type {number|null} */ (null),
    lastCompletedTrick: null,
    declarationPointsByTeam: /** @type {[number, number]} */ ([0, 0]),
    declarationGrossByTeam: /** @type {[number, number]} */ ([0, 0]),
    bellaBonusByTeam: /** @type {[number, number]} */ ([0, 0]),
    /** После первой взятки: один раз начислить Беллу по пулу рука+свой стол. */
    openDuellFirstTrickDeclarationsDone: false,
  };
}

/**
 * @param {any} state
 * @param {any[]} events
 * @returns {any}
 */
function finishRoundScoring(state, events) {
  const c = state.contract;
  if (!c) throw new Error("openDuell/engine: no contract at score");
  const trump = c.trumpSuit;
  const tcp = /** @type {[number, number]} */ ([
    state.trickCardsByTeam[0].reduce((a, card) => a + cardPointsTowardRoundTotal(card, trump), 0),
    state.trickCardsByTeam[1].reduce((a, card) => a + cardPointsTowardRoundTotal(card, trump), 0),
  ]);
  const lastTeam =
    state.lastTrickWinnerSeat == null ? 0 : teamOfSeat(state.lastTrickWinnerSeat);
  const decl = /** @type {[number, number]} */ ([
    state.declarationPointsByTeam[0] + state.bellaBonusByTeam[0],
    state.declarationPointsByTeam[1] + state.bellaBonusByTeam[1],
  ]);
  const baitLoserDecl = state.declarationGrossByTeam[c.contractTeam] + state.bellaBonusByTeam[c.contractTeam];
  const sr = scoreRound({
    contractTeam: c.contractTeam,
    contractTargetPoints: c.contractTargetPoints,
    contraMultiplier: c.contraMultiplier ?? 1,
    kapotBid: c.kapot,
    tricksWonByTeam: state.tricksWonByTeam,
    trickCardPointsByTeam: tcp,
    lastTrickWinnerTeam: lastTeam,
    declarationPointsByTeam: decl,
    losingContractTeamDeclarationPointsForBait: baitLoserDecl,
  });
  const teamScores = /** @type {[number, number]} */ ([
    state.teamScores[0] + sr.delta[0],
    state.teamScores[1] + sr.delta[1],
  ]);
  events.push({
    type: "ROUND_SCORED",
    payload: {
      delta: sr.delta,
      success: sr.success,
      bait: sr.bait,
      contractTeamTrickPlusDecl: sr.contractTeamTrickPlusDecl,
    },
  });
  let phase = "dealing";
  /** @type {any[]} */
  const more = [];
  if (teamScores[0] >= state.targetScore || teamScores[1] >= state.targetScore) {
    phase = "match_end";
    more.push({ type: "MATCH_ENDED", payload: { teamScores } });
  }
  const dealerSeat = /** @type {0|1} */ ((state.dealerSeat + 1) % 2);
  for (const m of more) events.push(m);
  return {
    ...state,
    phase,
    teamScores,
    dealerSeat,
    lastCompletedTrick: null,
    hands: [null, null],
    tableStacks: [null, null],
    auction: null,
    contract: null,
    trickIndex: 0,
    trickLeadSeat: null,
    currentTrick: [],
    currentTurnSeat: null,
    tricksWonByTeam: [0, 0],
    trickCardsByTeam: [[], []],
    lastTrickWinnerSeat: null,
    declarationPointsByTeam: [0, 0],
    declarationGrossByTeam: [0, 0],
    bellaBonusByTeam: [0, 0],
    openDuellFirstTrickDeclarationsDone: false,
    roundIndex: state.roundIndex + 1,
  };
}

/**
 * @param {any} state
 * @param {number} physicalSeat 0|1
 * @returns {number[]}
 */
function virtualPlayableCards(state, physicalSeat) {
  const ps = physicalSeat === 1 ? 1 : 0;
  const hand = state.hands[ps];
  const tabs = state.tableStacks[ps];
  if (!Array.isArray(hand) || !Array.isArray(tabs)) return [];
  return hand.concat(topCardsFromTable(tabs));
}

/**
 * Открытый 1×1: на местах 2/3 — только верхи столбцов. На 0/1 — пока в руке есть карты, только рука (иначе
 * `legalPlays` увидел бы масть со стола и обязал бы к ней без права сыграть её с руки). Когда рука пуста
 * (взятки 7–8), те же логические «руки» играют только с верхов стола.
 *
 * @param {any} state
 * @param {number} physicalSeat 0|1
 * @param {number} logicalTurnSeat `state.currentTurnSeat` (0..3)
 * @returns {number[]}
 */
function virtualPlayableForOpenDuellTurn(state, physicalSeat, logicalTurnSeat) {
  const ps = physicalSeat === 1 ? 1 : 0;
  const hand = state.hands[ps];
  const tabs = state.tableStacks[ps];
  if (!Array.isArray(hand) || !Array.isArray(tabs)) return [];
  const tops = topCardsFromTable(tabs);
  if (logicalTurnSeat === 2 || logicalTurnSeat === 3) {
    return tops.slice();
  }
  if (hand.length > 0) {
    return hand.slice();
  }
  return tops.slice();
}

/**
 * Инвариант: в розыгрыше у текущего места есть ≥1 легальный ход (после варианта A на 2/3 — только со стола).
 * @param {any} state
 */
function assertOpenDuellCurrentTurnHasLegalPlay(state) {
  const ls = state.currentTurnSeat;
  if (state.phase !== "play" || ls == null) return;
  const c = state.contract;
  const lead = state.trickLeadSeat;
  if (!c || lead == null) return;
  const ph = ls === 0 || ls === 2 ? 0 : 1;
  const virtual = virtualPlayableForOpenDuellTurn(state, ph, ls);
  const legal = legalPlays(virtual, state.currentTrick || [], c.trumpSuit, lead);
  if (!legal.length) {
    throw new Error(
      `openDuell/engine: no legal play for logical seat ${ls} (open 1×1: hand seats use hand while non-empty, else table tops; seats 2/3 use table tops)`
    );
  }
}

/**
 * @param {any} state
 * @param {any} cmd
 * @returns {{ state: any, events: any[] }}
 */
function applyCommand(state, cmd) {
  if (!state || typeof state !== "object") {
    throw new TypeError("openDuell/engine: state required");
  }
  if (!cmd || typeof cmd.type !== "string") {
    throw new Error("openDuell/engine: command required");
  }

  /** @type {any[]} */
  const events = [];

  if (state.phase === "match_end" && cmd.type !== "START_ROUND") {
    throw new Error("openDuell/engine: match ended — only START_ROUND");
  }

  if (cmd.type === "START_ROUND") {
    let base = state;
    if (state.phase === "match_end") {
      base = {
        ...state,
        phase: "init",
        teamScores: [0, 0],
        roundIndex: 0,
      };
    }
    if (base.phase !== "init" && base.phase !== "dealing") {
      throw new Error("openDuell/engine: START_ROUND only from init, dealing, or match_end");
    }
    if (hasMatchWinner(base) && base.phase === "dealing") {
      throw new Error("openDuell/engine: match already won");
    }
    const dealt = dealOpenDuell(base.rng);
    const stacks0 = columnsToStacks(dealt.tableColumns[0]);
    const stacks1 = columnsToStacks(dealt.tableColumns[1]);
    const dealerSeat = base.dealerSeat === 1 ? 1 : 0;
    const auction = createInitialAuction2p(dealerSeat);
    return {
      state: {
        ...base,
        phase: "auction",
        hands: [dealt.hands[0].slice(), dealt.hands[1].slice()],
        tableStacks: [cloneTableStacks(stacks0), cloneTableStacks(stacks1)],
        auction,
        contract: null,
        tricksWonByTeam: [0, 0],
        trickCardsByTeam: [[], []],
        trickIndex: 0,
        currentTrick: [],
        trickLeadSeat: null,
        currentTurnSeat: auction.turnSeat,
        lastTrickWinnerSeat: null,
        lastCompletedTrick: null,
        openDuellFirstTrickDeclarationsDone: false,
      },
      events,
    };
  }

  if (
    state.phase === "auction" &&
    (cmd.type === "BID" || cmd.type === "PASS")
  ) {
    const seat = Number(cmd.seat);
    if (seat !== 0 && seat !== 1) {
      throw new Error("openDuell/engine: auction seat must be 0 or 1");
    }
    if (!state.auction) throw new Error("openDuell/engine: no auction");
    if (state.currentTurnSeat !== seat) {
      throw new Error("openDuell/engine: auction — not your turn");
    }
    const dealerSeat = state.dealerSeat === 1 ? 1 : 0;
    const aucCmd =
      cmd.type === "BID"
        ? {
            type: "BID",
            seat: /** @type {0|1} */ (seat),
            bid: cmd.bid || cmd.payload?.bid,
          }
        : { type: "PASS", seat: /** @type {0|1} */ (seat) };
    if (cmd.type === "BID" && !aucCmd.bid) {
      throw new Error("openDuell/engine: BID requires bid");
    }
    const ar = applyAuction2pCommand(state.auction, aucCmd, dealerSeat);
    let next = {
      ...state,
      auction: ar.state,
      currentTurnSeat: ar.state.turnSeat,
    };

    if (ar.redealt) {
      events.push({ type: "AUCTION_REDEAL", payload: {} });
      const dealt = dealOpenDuell(state.rng);
      const stacks0 = columnsToStacks(dealt.tableColumns[0]);
      const stacks1 = columnsToStacks(dealt.tableColumns[1]);
      next = {
        ...next,
        hands: [dealt.hands[0].slice(), dealt.hands[1].slice()],
        tableStacks: [cloneTableStacks(stacks0), cloneTableStacks(stacks1)],
        auction: ar.state,
        currentTurnSeat: ar.state.turnSeat,
        openDuellFirstTrickDeclarationsDone: false,
      };
      return { state: next, events };
    }

    if (ar.closed && ar.state.phase === "closed") {
      const b = ar.state.highestBid;
      const bidAuthorSeat = ar.state.bidAuthorSeat;
      if (!b || bidAuthorSeat == null) {
        throw new Error("openDuell/engine: auction closed without bid");
      }
      const trumpSuit =
        b.trump === TRUMP_NO_TRUMP ? null : b.trump === TRUMP_ALL_TRUMP ? TRUMP_ALL_TRUMP : b.trump;
      const contractTargetPoints = b.kapot ? KAPOT_CONTRACT_POINTS : b.tens * 10;
      const contractTeam = teamOfSeat(bidAuthorSeat);
      const rawLead = (dealerSeat + 1) % 4;
      const lead = normalizeTurnSeatForOpenDuellEmptyHand(next, rawLead);
      next = {
        ...next,
        phase: "play",
        auction: ar.state,
        contract: {
          bidAuthorSeat,
          tens: b.tens,
          trump: b.trump,
          kapot: Boolean(b.kapot),
          trumpSuit,
          contractTeam,
          contractTargetPoints,
          contraMultiplier: 1,
        },
        trickIndex: 1,
        trickLeadSeat: lead,
        currentTrick: [],
        currentTurnSeat: lead,
        lastCompletedTrick: null,
        openDuellFirstTrickDeclarationsDone: false,
      };
      assertOpenDuellCurrentTurnHasLegalPlay(next);
      events.push({
        type: "AUCTION_ENDED",
        payload: { bid: b, bidAuthorSeat },
      });
      events.push({
        type: "TRICK_STARTED",
        payload: { trickIndex: 1, trickLeadSeat: lead },
      });
      return { state: next, events };
    }

    return { state: next, events };
  }

  if (cmd.type === "PLAY_CARD") {
    if (state.phase !== "play") throw new Error("openDuell/engine: PLAY_CARD only in play");
    const physicalSeat = Number(cmd.seat);
    if (physicalSeat !== 0 && physicalSeat !== 1) {
      throw new Error("openDuell/engine: PLAY_CARD seat must be 0 or 1");
    }
    const pl = cmd.payload || {};
    const from = pl.from === "table" ? "table" : "hand";
    const card = Number(pl.card);
    if (!Number.isInteger(card)) {
      throw new Error("openDuell/engine: PLAY_CARD needs integer payload.card");
    }
    const logicalSeat = from === "hand" ? physicalSeat : physicalSeat + 2;
    if (logicalSeat !== state.currentTurnSeat) {
      throw new Error("openDuell/engine: not this seat's turn");
    }
    const c = state.contract;
    if (!c || !state.hands || !state.tableStacks) {
      throw new Error("openDuell/engine: invalid play state");
    }
    const leadSeat = state.trickLeadSeat;
    if (leadSeat == null) throw new Error("openDuell/engine: no lead seat");

    const virtual = virtualPlayableForOpenDuellTurn(state, physicalSeat, state.currentTurnSeat);
    const legal = legalPlays(virtual, state.currentTrick, c.trumpSuit, leadSeat);
    if (!legal.includes(card)) {
      throw new Error("openDuell/engine: illegal card");
    }

    if (from === "hand") {
      const hand = state.hands[physicalSeat];
      const idx = hand.indexOf(card);
      if (idx < 0) throw new Error("openDuell/engine: card not in hand");
      const newHand = hand.filter((_, i) => i !== idx);
      state = {
        ...state,
        hands: state.hands.map((row, si) => (si === physicalSeat ? newHand : row)),
      };
    } else {
      const col = Number(pl.columnIndex);
      if (!Number.isInteger(col) || col < 0 || col > 4) {
        throw new Error("openDuell/engine: table play needs payload.columnIndex 0..4");
      }
      const tabs = state.tableStacks[physicalSeat];
      const stack = tabs[col];
      if (!stack.length || stack[stack.length - 1] !== card) {
        throw new Error("openDuell/engine: must play visible top of column");
      }
      const newStack = stack.slice(0, -1);
      const newTabs = tabs.map((s, i) => (i === col ? newStack : s));
      state = {
        ...state,
        tableStacks: state.tableStacks.map((row, si) => (si === physicalSeat ? newTabs : row)),
      };
    }

    /** @type {any} */
    let working = state;
    const trick = [...working.currentTrick, { seat: logicalSeat, card }];
    events.push({
      type: "CARD_PLAYED",
      payload: { seat: logicalSeat, physicalSeat, from, columnIndex: pl.columnIndex, card, trickIndex: working.trickIndex },
    });

    if (trick.length < 4) {
      const L = working.trickLeadSeat;
      if (L == null) throw new Error("openDuell/engine: trickLeadSeat required during play");
      const nextSeat = nextOpenDuellPartialTrickTurnSeat(working, L, trick.length);
      const afterPlay = {
        ...working,
        currentTrick: trick,
        currentTurnSeat: nextSeat,
      };
      assertOpenDuellCurrentTurnHasLegalPlay(afterPlay);
      return {
        state: afterPlay,
        events,
      };
    }

    const winnerSeat = trickWinner(trick, c.trumpSuit);
    const lastCompletedTrick = {
      trickIndex: working.trickIndex,
      winnerSeat,
      plays: trick.map((p) => ({ seat: p.seat, card: p.card })),
    };
    const wTeam = teamOfSeat(winnerSeat);
    const tricksWonByTeam = /** @type {[number, number]} */ ([
      working.tricksWonByTeam[0] + (wTeam === 0 ? 1 : 0),
      working.tricksWonByTeam[1] + (wTeam === 1 ? 1 : 0),
    ]);
    const trickCardsByTeam = /** @type {[number[], number[]]} */ ([
      working.trickCardsByTeam[0].concat(trick.filter((p) => teamOfSeat(p.seat) === 0).map((p) => p.card)),
      working.trickCardsByTeam[1].concat(trick.filter((p) => teamOfSeat(p.seat) === 1).map((p) => p.card)),
    ]);
    events.push({
      type: "TRICK_WON",
      payload: {
        winnerSeat,
        trickIndex: working.trickIndex,
        trick,
      },
    });

    /** @type {[number, number]} */
    let bellaBonusByTeam = [
      Number(working.bellaBonusByTeam?.[0]) || 0,
      Number(working.bellaBonusByTeam?.[1]) || 0,
    ];
    /** @type {[number, number]} */
    let declarationPointsByTeam = [
      Number(working.declarationPointsByTeam?.[0]) || 0,
      Number(working.declarationPointsByTeam?.[1]) || 0,
    ];
    /** @type {[number, number]} */
    let declarationGrossByTeam = [
      Number(working.declarationGrossByTeam?.[0]) || 0,
      Number(working.declarationGrossByTeam?.[1]) || 0,
    ];
    let openDuellFirstTrickDeclarationsDone = Boolean(working.openDuellFirstTrickDeclarationsDone);

    if (working.trickIndex === 1 && !openDuellFirstTrickDeclarationsDone && working.hands && working.tableStacks) {
      const resolved = resolveFirstTrickOpenDeclarations(working);
      bellaBonusByTeam = resolved.bellaBonusByTeam;
      declarationPointsByTeam = resolved.declarationPointsByTeam;
      declarationGrossByTeam = resolved.declarationGrossByTeam;
      openDuellFirstTrickDeclarationsDone = true;
      const { bella, combos } = resolved;
      events.push({
        type: "OPEN_DUELL_DECLARATIONS_RESOLVED",
        payload: {
          bella: {
            delta: bella.delta,
            teamsWithBella: bella.teamsWithBella,
          },
          bellaBonusByTeam,
          declarationPointsByTeam,
          declarationGrossByTeam,
          combos: {
            outcome: combos.compare.outcome,
            grossByTeam: combos.declarationGrossByTeam,
            scoringByTeam: combos.declarationPointsByTeam,
            labelsScoringA: combos.compare.scoringA.map((d) => d.label),
            labelsScoringB: combos.compare.scoringB.map((d) => d.label),
          },
        },
      });
    }

    const postTrickMeta = {
      bellaBonusByTeam,
      declarationPointsByTeam,
      declarationGrossByTeam,
      openDuellFirstTrickDeclarationsDone,
    };

    const isLast = working.trickIndex === 8;
    if (!isLast) {
      const nextIdx = working.trickIndex + 1;
      const leadSeatNorm = normalizeTurnSeatForOpenDuellEmptyHand(working, winnerSeat);
      events.push({
        type: "TRICK_STARTED",
        payload: { trickIndex: nextIdx, trickLeadSeat: leadSeatNorm },
      });
      const afterTrick = {
        ...working,
        ...postTrickMeta,
        currentTrick: [],
        trickIndex: nextIdx,
        trickLeadSeat: leadSeatNorm,
        currentTurnSeat: leadSeatNorm,
        tricksWonByTeam,
        trickCardsByTeam,
        lastTrickWinnerSeat: winnerSeat,
        lastCompletedTrick,
      };
      assertOpenDuellCurrentTurnHasLegalPlay(afterTrick);
      return {
        state: afterTrick,
        events,
      };
    }

    let nextState = {
      ...working,
      ...postTrickMeta,
      currentTrick: [],
      trickIndex: 10,
      trickLeadSeat: winnerSeat,
      currentTurnSeat: null,
      tricksWonByTeam,
      trickCardsByTeam,
      lastTrickWinnerSeat: winnerSeat,
      lastCompletedTrick,
    };
    const scored = finishRoundScoring(nextState, events);
    return { state: scored, events };
  }

  throw new Error(`openDuell/engine: unsupported command ${cmd.type} in phase ${state.phase}`);
}

module.exports = {
  ALLOWED_TARGET_SCORES,
  DEFAULT_TARGET_SCORE,
  createInitialState,
  applyCommand,
  virtualPlayableCards,
  virtualPlayableForOpenDuellTurn,
  assertOpenDuellCurrentTurnHasLegalPlay,
  topCardsFromTable,
  columnIndexForTopCard,
  normalizeTurnSeatForOpenDuellEmptyHand,
  nextOpenDuellPlaySeat,
  nextOpenDuellPartialTrickTurnSeat,
};
